import React, { Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import EpRulanticaLoader from './EpRulanticaLoader';
import { AttractionsSkeleton, RestaurantsSkeleton, ShowsSkeleton } from './skeletons';
import {
  LOADER_TYPES,
  SKELETON_TYPES,
  getParkLoadingDescriptor
} from '../utils/parkLoadingFallbackRules';

const LegolandLoader = React.lazy(() => import('./LegolandLoader'));

const MickeyLoader = () => (
  <div className="mickey-loading" aria-hidden="true">
    <div className="ear ear-left"></div>
    <div className="ear ear-right"></div>
    <div className="head"></div>
  </div>
);

const UniversalLoader = () => (
  <div className="universal-loading">
    <img src="/images/universal-studiosFL-loading.png" alt="Chargement Universal" className="universal-logo" />
  </div>
);

const ImageLoader = ({ wrapperClassName, sources, src, alt, imageClassName }) => (
  <div className={wrapperClassName}>
    <picture>
      {sources?.map((source) => (
        <source key={source.srcSet} srcSet={source.srcSet} type={source.type} />
      ))}
      <img src={src} alt={alt} className={imageClassName} />
    </picture>
  </div>
);

const LoaderArtwork = ({ loaderType }) => {
  switch (loaderType) {
    case LOADER_TYPES.MICKEY:
      return <MickeyLoader />;

    case LOADER_TYPES.UNIVERSAL:
      return <UniversalLoader />;

    case LOADER_TYPES.ASTERIX:
      return (
        <div className="asterix-loading">
          <img src="/images/loading-idefix.svg" alt="Chargement Parc Asterix" />
        </div>
      );

    case LOADER_TYPES.EFTELING:
      return (
        <div className="efteling-loading">
          <div className="efteling-line"></div>
        </div>
      );

    case LOADER_TYPES.PHANTASIALAND:
      return (
        <div className="phantasialand-loading">
          <div className="train-container">
            <img src="/images/taron-loading.png" alt="Taron wagon 1" className="wagon" />
            <img src="/images/taron-loading.png" alt="Taron wagon 2" className="wagon" />
            <img src="/images/taron-loading.png" alt="Taron wagon 3" className="wagon" />
            <img src="/images/taron-loading.png" alt="Taron wagon 4" className="wagon" />
          </div>
        </div>
      );

    case LOADER_TYPES.TOVERLAND:
      return (
        <div className="toverland-loading">
          <div className="magic-wand">
            <div className="wand-tip"></div>
          </div>
          <div className="magic-stars">
            <div className="magic-star">*</div>
            <div className="magic-star">*</div>
            <div className="magic-star">*</div>
            <div className="magic-star">*</div>
            <div className="magic-star">*</div>
          </div>
          <div className="magic-amulet">
            <div className="amulet-symbol">*</div>
          </div>
        </div>
      );

    case LOADER_TYPES.NIGLOLAND:
      return (
        <div className="nigloland-loading">
          <img src="/images/niglo-loading.png" alt="Chargement Nigloland" className="niglo-mascot" />
        </div>
      );

    case LOADER_TYPES.LE_PAL:
      return (
        <ImageLoader
          wrapperClassName="le-pal-loading"
          sources={[{ srcSet: '/images/webp/loader-lepal.webp', type: 'image/webp' }]}
          src="/images/loader-lepal.png"
          alt="Chargement Le Pal"
          imageClassName="le-pal-logo"
        />
      );

    case LOADER_TYPES.ENERGYLANDIA:
      return (
        <ImageLoader
          wrapperClassName="energylandia-loading"
          sources={[{ srcSet: '/images/webp/loader-el.webp', type: 'image/webp' }]}
          src="/images/loader-el.png"
          alt="Chargement Energylandia"
          imageClassName="energylandia-logo"
        />
      );

    case LOADER_TYPES.GRONA_LUND:
      return (
        <ImageLoader
          wrapperClassName="grona-lund-loading"
          sources={[{ srcSet: '/images/webp/loader-gronalund.webp', type: 'image/webp' }]}
          src="/images/loader-gronalund.png"
          alt="Chargement Grona Lund"
          imageClassName="grona-lund-logo"
        />
      );

    case LOADER_TYPES.SEAWORLD:
      return (
        <div className="seaworld-loading">
          <div className="circle">
            <div className="wave"></div>
          </div>
        </div>
      );

    case LOADER_TYPES.BUSCH_GARDENS:
      return (
        <div className="busch-loading">
          <img src="/images/bush-loading.png" alt="Chargement Busch Gardens Tampa" className="busch-logo" />
        </div>
      );

    case LOADER_TYPES.LEGOLAND_FLORIDA:
      return (
        <Suspense fallback={<SkeletonArtwork skeletonType={SKELETON_TYPES.ATTRACTIONS} />}>
          <LegolandLoader />
        </Suspense>
      );

    case LOADER_TYPES.EP_RULANTICA:
      return <EpRulanticaLoader />;

    case LOADER_TYPES.PLOPSALAND:
      return (
        <div className="plopsaland-loading">
          <img src="/images/logo-plopsapanne.png" alt="Chargement Plopsaland" className="plopsaland-logo" />
        </div>
      );

    case LOADER_TYPES.PORTAVENTURA:
      return (
        <div className="portaventura-loading">
          <img src="/images/portaventuraworld-loading.png" alt="Chargement PortAventura World" className="portaventura-logo" />
        </div>
      );

    case LOADER_TYPES.FERRARI_LAND:
      return (
        <div className="ferrari-land-loading">
          <picture>
            <source srcSet="/images/logos/webp/logo-ferrariland.webp" type="image/webp" />
            <img src="/images/logos/logo-ferrariland.png" alt="Chargement Ferrari Land" className="ferrari-land-logo" />
          </picture>
        </div>
      );

    case LOADER_TYPES.WALIBI:
      return (
        <div className="walibi-loading">
          <img src="/images/walibi-loading.png" alt="Chargement Walibi" className="walibi-logo" />
        </div>
      );

    case LOADER_TYPES.THORPE_PARK:
      return (
        <ImageLoader
          wrapperClassName="thorpe-park-loading"
          sources={[{ srcSet: '/images/webp/loader-thorpe-park.webp', type: 'image/webp' }]}
          src="/images/loader-thorpe-park.png"
          alt="Chargement Thorpe Park"
          imageClassName="thorpe-park-logo"
        />
      );

    case LOADER_TYPES.BELLEWAERDE:
      return (
        <ImageLoader
          wrapperClassName="bellewaerde-loading"
          sources={[{ srcSet: '/images/webp/loader-bellewaerde.webp', type: 'image/webp' }]}
          src="/images/loader-bellewaerde.png"
          alt="Chargement Bellewaerde"
          imageClassName="bellewaerde-logo"
        />
      );

    case LOADER_TYPES.BOBBEJAANLAND:
      return (
        <ImageLoader
          wrapperClassName="bobbejaanland-loading"
          sources={[{ srcSet: '/images/webp/loader-bobbejaanland.webp', type: 'image/webp' }]}
          src="/images/loader-bobbejaanland.png"
          alt="Chargement Bobbejaanland"
          imageClassName="bobbejaanland-logo"
        />
      );

    case LOADER_TYPES.GARDALAND:
      return (
        <ImageLoader
          wrapperClassName="gardaland-loading"
          sources={[{ srcSet: '/images/webp/loader-gardaland.webp', type: 'image/webp' }]}
          src="/images/loader-gardaland.png"
          alt="Chargement Gardaland"
          imageClassName="gardaland-logo"
        />
      );

    case LOADER_TYPES.MIRABILANDIA:
      return (
        <ImageLoader
          wrapperClassName="mirabilandia-loading"
          sources={[{ srcSet: '/images/webp/loader-mirabilandia.webp', type: 'image/webp' }]}
          src="/images/loader-mirabilandia.png"
          alt="Chargement Mirabilandia"
          imageClassName="mirabilandia-logo"
        />
      );

    case LOADER_TYPES.MOVIE_PARK_GERMANY:
      return (
        <ImageLoader
          wrapperClassName="movie-park-germany-loading"
          sources={[{ srcSet: '/images/webp/loader-movie-park-germany.webp', type: 'image/webp' }]}
          src="/images/loader-movie-park-germany.png"
          alt="Chargement Movie Park Germany"
          imageClassName="movie-park-germany-logo"
        />
      );

    case LOADER_TYPES.PARQUE_DE_ATRACCIONES_DE_MADRID:
      return (
        <ImageLoader
          wrapperClassName="parque-de-atracciones-de-madrid-loading"
          sources={[{ srcSet: '/images/webp/loader-parque-de-atracciones-de-madrid.webp', type: 'image/webp' }]}
          src="/images/loader-parque-de-atracciones-de-madrid.png"
          alt="Chargement Parque de Atracciones de Madrid"
          imageClassName="parque-de-atracciones-de-madrid-logo"
        />
      );

    case LOADER_TYPES.LOTTE_WORLD:
      return (
        <ImageLoader
          wrapperClassName="lotte-world-loading"
          sources={[{ srcSet: '/images/webp/loader-lotteworld.webp', type: 'image/webp' }]}
          src="/images/loader-lotteworld.png"
          alt="Chargement Lotte World"
          imageClassName="lotte-world-logo"
        />
      );

    case LOADER_TYPES.KNOTTS:
      return (
        <div className="knotts-loading">
          <img src="/images/knotts-loader.png" alt="Chargement Knott's Berry Farm" className="knotts-logo" />
        </div>
      );

    default:
      return null;
  }
};

const SkeletonArtwork = ({ skeletonType }) => {
  if (skeletonType === SKELETON_TYPES.RESTAURANTS) {
    return <RestaurantsSkeleton count={6} />;
  }

  if (skeletonType === SKELETON_TYPES.SHOWS) {
    return <ShowsSkeleton count={5} />;
  }

  return <AttractionsSkeleton count={8} />;
};

const useResolvedDescriptor = (pathname) => {
  const location = useLocation();
  return getParkLoadingDescriptor(pathname || location.pathname);
};

export const ParkLoadingOverlay = ({ pathname, visible = true }) => {
  const descriptor = useResolvedDescriptor(pathname);

  if (!descriptor.isBranded) {
    return null;
  }

  return (
    <div
      className={`loading-overlay ${visible ? 'visible' : 'hidden'}`}
      role="status"
      aria-live="polite"
      aria-busy={visible ? 'true' : 'false'}
    >
      <div className="park-loader-frame" data-loader-type={descriptor.loaderType}>
        <LoaderArtwork loaderType={descriptor.loaderType} />
      </div>
    </div>
  );
};

const ParkLoadingFallback = ({ pathname }) => {
  const descriptor = useResolvedDescriptor(pathname);

  if (descriptor.isBranded) {
    return <ParkLoadingOverlay pathname={pathname} visible={true} />;
  }

  return (
    <main className="park-route-skeleton-fallback park-content loaded" role="status" aria-live="polite" aria-busy="true">
      <SkeletonArtwork skeletonType={descriptor.skeletonType} />
    </main>
  );
};

export default ParkLoadingFallback;
